import * as duckdb from '@duckdb/duckdb-wasm';
import duckdb_wasm from '@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm?url';
import duckdb_wasm_eh from '@duckdb/duckdb-wasm/dist/duckdb-eh.wasm?url';
import * as arrow from 'apache-arrow';
import { debugLog } from '../utils/debug-logger';
import logger from '../utils/logger';

export interface DatasetMetadata {
  id: string;
  name: string;
  rowCount: number;
  columnCount: number;
  columns: ColumnMetadata[];
  size: number;
  uploadedAt: Date;
  lastQueried?: Date;
}

export interface ColumnMetadata {
  name: string;
  type: string;
  nullable: boolean;
  unique: number;
  nullCount: number;
  stats?: {
    min?: any;
    max?: any;
    mean?: number;
    median?: number;
    stdDev?: number;
  };
}

export interface QueryResult {
  data: any[];
  schema: arrow.Schema;
  rowCount: number;
  executionTime: number;
}

export interface StreamingProgress {
  bytesProcessed: number;
  totalBytes: number;
  rowsProcessed: number;
  estimatedRows: number;
  percentage: number;
  stage: 'uploading' | 'parsing' | 'indexing' | 'analyzing' | 'complete';
}

class DuckDBEngine {
  private db: duckdb.AsyncDuckDB | null = null;
  private conn: duckdb.AsyncDuckDBConnection | null = null;
  private initialized = false;
  private currentDataset: DatasetMetadata | null = null;

  /**
   * Initialize DuckDB-WASM with optimal configuration
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
      
      // Select bundle based on browser capabilities
      const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);
      
      const worker_url = URL.createObjectURL(
        new Blob([`importScripts("${bundle.mainWorker}");`], {
          type: 'text/javascript',
        })
      );

      const worker = new Worker(worker_url);
      const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
      
      this.db = new duckdb.AsyncDuckDB(logger, worker);
      await this.db.instantiate(bundle.mainModule, bundle.pthreadWorker);
      
      // Optimize for large datasets
      await this.db.open({
        query: {
          castTimestampToDate: true,
          castBigIntToDouble: false,
        },
      });

      this.conn = await this.db.connect();
      
      // Set memory limit to 4GB (adaptive based on available RAM)
      await this.conn.query('SET memory_limit = \'4GB\';');
      // Note: DuckDB-WASM built without pthread support, threads config not available
      
      this.initialized = true;
      debugLog('✅ DuckDB-WASM initialized successfully');
    } catch (error) {
      logger.error('❌ Failed to initialize DuckDB-WASM:', error);
      throw new Error(`DuckDB initialization failed: ${error}`);
    }
  }

  /**
   * Load CSV file with streaming support and progress tracking
   */
  async loadCSVFile(
    file: File,
    onProgress?: (progress: StreamingProgress) => void
  ): Promise<DatasetMetadata> {
    if (!this.conn) throw new Error('DuckDB not initialized');

    const startTime = performance.now();
    const tableName = `dataset_${Date.now()}`;
    
    try {
      // Stage 1: Upload and register file
      onProgress?.({
        bytesProcessed: 0,
        totalBytes: file.size,
        rowsProcessed: 0,
        estimatedRows: Math.floor(file.size / 100), // Rough estimate
        percentage: 0,
        stage: 'uploading',
      });

      // Register file with DuckDB
      await this.db!.registerFileHandle(
        file.name,
        file,
        duckdb.DuckDBDataProtocol.BROWSER_FILEREADER,
        true
      );

      // Stage 2: Parse and create table
      onProgress?.({
        bytesProcessed: file.size * 0.3,
        totalBytes: file.size,
        rowsProcessed: 0,
        estimatedRows: Math.floor(file.size / 100),
        percentage: 30,
        stage: 'parsing',
      });

      // Auto-detect and create table
      await this.conn.query(`
        CREATE TABLE ${tableName} AS 
        SELECT * FROM read_csv_auto('${file.name}', 
          HEADER=TRUE,
          SAMPLE_SIZE=50000,
          AUTO_DETECT=TRUE,
          NORMALIZE_NAMES=TRUE,
          IGNORE_ERRORS=TRUE
        );
      `);

      // Stage 3: Analyze table
      onProgress?.({
        bytesProcessed: file.size * 0.6,
        totalBytes: file.size,
        rowsProcessed: 0,
        estimatedRows: Math.floor(file.size / 100),
        percentage: 60,
        stage: 'analyzing',
      });

      // Get metadata
      const metadata = await this.analyzeTable(tableName, file);
      
      // Set currentDataset BEFORE creating indexes
      this.currentDataset = metadata;

      // Stage 4: Create indexes
      onProgress?.({
        bytesProcessed: file.size * 0.9,
        totalBytes: file.size,
        rowsProcessed: metadata.rowCount,
        estimatedRows: metadata.rowCount,
        percentage: 90,
        stage: 'indexing',
      });

      // Create indexes on detected key columns
      await this.createIndexes(tableName, metadata.columns);

      // Complete
      onProgress?.({
        bytesProcessed: file.size,
        totalBytes: file.size,
        rowsProcessed: metadata.rowCount,
        estimatedRows: metadata.rowCount,
        percentage: 100,
        stage: 'complete',
      });
      
      const loadTime = performance.now() - startTime;
      debugLog(`✅ Dataset loaded in ${(loadTime / 1000).toFixed(2)}s`);
      
      return metadata;
    } catch (error) {
      logger.error('❌ Failed to load CSV:', error);
      throw new Error(`CSV loading failed: ${error}`);
    }
  }

  /**
   * Analyze table structure and statistics
   */
  private async analyzeTable(
    tableName: string,
    file: File
  ): Promise<DatasetMetadata> {
    if (!this.conn) throw new Error('DuckDB not initialized');

    // Get row count
    const countResult = await this.conn.query(
      `SELECT COUNT(*) as count FROM ${tableName}`
    );
    const rowCount = countResult.toArray()[0].count;

    // Get column information
    const schemaResult = await this.conn.query(`
      SELECT 
        column_name,
        data_type,
        is_nullable
      FROM information_schema.columns 
      WHERE table_name = '${tableName}'
    `);

    const columns: ColumnMetadata[] = [];
    for (const col of schemaResult.toArray()) {
      const stats = await this.getColumnStats(tableName, col.column_name, col.data_type);
      columns.push({
        name: col.column_name,
        type: col.data_type,
        nullable: col.is_nullable === 'YES',
        unique: stats.unique,
        nullCount: stats.nullCount,
        stats: stats.stats,
      });
    }

    return {
      id: tableName,
      name: file.name,
      rowCount: Number(rowCount),
      columnCount: columns.length,
      columns,
      size: file.size,
      uploadedAt: new Date(),
    };
  }

  /**
   * Get statistics for a specific column
   */
  private async getColumnStats(
    tableName: string,
    columnName: string,
    dataType: string
  ): Promise<any> {
    if (!this.conn) throw new Error('DuckDB not initialized');

    const uniqueResult = await this.conn.query(
      `SELECT COUNT(DISTINCT "${columnName}") as unique_count FROM ${tableName}`
    );
    const unique = uniqueResult.toArray()[0].unique_count;

    const nullResult = await this.conn.query(
      `SELECT COUNT(*) as null_count FROM ${tableName} WHERE "${columnName}" IS NULL`
    );
    const nullCount = nullResult.toArray()[0].null_count;

    let stats = undefined;

    // Get numeric statistics
    if (['INTEGER', 'BIGINT', 'DOUBLE', 'DECIMAL', 'FLOAT'].includes(dataType.toUpperCase())) {
      try {
        const statsResult = await this.conn.query(`
          SELECT 
            MIN("${columnName}") as min,
            MAX("${columnName}") as max,
            AVG("${columnName}") as mean,
            MEDIAN("${columnName}") as median,
            STDDEV("${columnName}") as stddev
          FROM ${tableName}
          WHERE "${columnName}" IS NOT NULL
        `);
        const row = statsResult.toArray()[0];
        stats = {
          min: row.min,
          max: row.max,
          mean: row.mean,
          median: row.median,
          stdDev: row.stddev,
        };
      } catch (e) {
        // Ignore if stats cannot be computed
      }
    }

    return { unique: Number(unique), nullCount: Number(nullCount), stats };
  }

  /**
   * Create indexes on key columns for faster queries
   */
  private async createIndexes(
    tableName: string,
    columns: ColumnMetadata[]
  ): Promise<void> {
    if (!this.conn) throw new Error('DuckDB not initialized');
    if (!this.currentDataset) {
      logger.warn('⚠️ currentDataset is null, skipping index creation');
      return;
    }

    // Index columns with high uniqueness (likely keys/IDs)
    for (const col of columns) {
      const uniquenessRatio = col.unique / this.currentDataset.rowCount;
      if (uniquenessRatio > 0.9 && col.unique > 100) {
        try {
          // DuckDB doesn't need explicit indexes, but we can create them for very selective columns
          // This is more for demonstration - DuckDB auto-optimizes
          debugLog(`🔑 High cardinality column detected: ${col.name}`);
        } catch (e) {
          // Ignore index creation errors
        }
      }
    }
  }

  /**
   * Execute SQL query and return results
   */
  async query(sql: string): Promise<QueryResult> {
    if (!this.conn) throw new Error('DuckDB not initialized');

    const startTime = performance.now();
    
    try {
      const result = await this.conn.query(sql);
      const executionTime = performance.now() - startTime;
      
      const data = result.toArray();
      const schema = result.schema;
      
      return {
        data,
        schema,
        rowCount: data.length,
        executionTime,
      };
    } catch (error) {
      logger.error('❌ Query failed:', error);
      throw new Error(`Query execution failed: ${error}`);
    }
  }

  /**
   * Execute query and return results as Arrow table (more efficient)
   */
  async queryArrow(sql: string): Promise<arrow.Table> {
    if (!this.conn) throw new Error('DuckDB not initialized');

    try {
      const result = await this.conn.query(sql);
      return result;
    } catch (error) {
      logger.error('❌ Query failed:', error);
      throw new Error(`Query execution failed: ${error}`);
    }
  }

  /**
   * Get sample data from current dataset
   */
  async getSample(limit: number = 100): Promise<any[]> {
    if (!this.currentDataset) throw new Error('No dataset loaded');

    const result = await this.query(
      `SELECT * FROM ${this.currentDataset.id} LIMIT ${limit}`
    );
    return result.data;
  }

  /**
   * Export query results to CSV
   */
  async exportToCSV(sql: string): Promise<Blob> {
    if (!this.conn) throw new Error('DuckDB not initialized');

    const result = await this.query(sql);
    const rows = result.data;
    
    if (rows.length === 0) {
      throw new Error('No data to export');
    }

    // Create CSV content
    const headers = Object.keys(rows[0]);
    const csvLines = [headers.join(',')];
    
    for (const row of rows) {
      const values = headers.map(h => {
        const val = row[h];
        if (val === null || val === undefined) return '';
        if (typeof val === 'string' && val.includes(',')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      });
      csvLines.push(values.join(','));
    }

    return new Blob([csvLines.join('\n')], { type: 'text/csv' });
  }

  /**
   * Get current dataset metadata
   */
  getCurrentDataset(): DatasetMetadata | null {
    return this.currentDataset;
  }

  /**
   * Close connection and cleanup
   */
  async close(): Promise<void> {
    if (this.conn) {
      await this.conn.close();
      this.conn = null;
    }
    if (this.db) {
      await this.db.terminate();
      this.db = null;
    }
    this.initialized = false;
    this.currentDataset = null;
  }

  /**
   * Drop current table and clear dataset
   */
  async clearDataset(): Promise<void> {
    if (this.currentDataset && this.conn) {
      try {
        await this.conn.query(`DROP TABLE IF EXISTS ${this.currentDataset.id}`);
        this.currentDataset = null;
      } catch (error) {
        logger.error('Error clearing dataset:', error);
      }
    }
  }
}

// Singleton instance
let engineInstance: DuckDBEngine | null = null;

export const getDuckDBEngine = (): DuckDBEngine => {
  if (!engineInstance) {
    engineInstance = new DuckDBEngine();
  }
  return engineInstance;
};

export default DuckDBEngine;

